// Schemas for routes-models.ts (browser-safe zod). Platform-owned model providers and per-purpose assignments.
import { z } from 'zod';
import { GlobalReleaseSummarySchema } from './api.js';
import { LlmStateSchema, TestRunSchema } from './api-agent-runtime.js';
import { MODEL_PURPOSES, ModelProviderKindSchema, ModelPurposeSchema } from './enums.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);

export const MODEL_PURPOSE_LABELS: Record<(typeof MODEL_PURPOSES)[number], string> = {
  whatsapp_agent: 'Agente de WhatsApp',
  voice_agent: 'Agente de voz',
  summaries: 'Resúmenes de conversación',
  audits: 'Auditorías de asesores',
  vision: 'Descripción de imágenes y PDF',
  copilot: 'Copiloto de llamadas',
};

export const MODEL_PROVIDER_KIND_LABELS = { anthropic: 'Anthropic', openai: 'OpenAI', openai_compatible: 'Compatible con OpenAI' } as const;

export const ModelProviderTestStatusSchema = z.enum(['untested', 'ok', 'failed']);

/** A provider account. The API key is never returned — only its last characters. */
export const ModelProviderSchema = z.object({
  id: Id,
  name: z.string(),
  kind: ModelProviderKindSchema,
  baseUrl: z.string().nullable(),
  keyHint: z.string(),
  testStatus: ModelProviderTestStatusSchema,
  testDetail: z.string().nullable(),
  testModel: z.string().nullable(),
  testLatencyMs: z.number().int().nullable(),
  testedAt: Iso.nullable(),
  archived: z.boolean(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type ModelProvider = z.infer<typeof ModelProviderSchema>;

const Name = z.string().trim().min(2).max(60);
const BaseUrl = z.string().trim().url().max(300);
const ApiKey = z.string().trim().min(8).max(500);
const ModelId = z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9._:/@-]+$/, 'Identificador de modelo inválido');

export const CreateModelProviderSchema = z
  .object({ name: Name, kind: ModelProviderKindSchema, baseUrl: BaseUrl.nullable().optional(), apiKey: ApiKey })
  .refine((v) => v.kind !== 'openai_compatible' || !!v.baseUrl, { path: ['baseUrl'], message: 'Un proveedor compatible con OpenAI necesita la URL base' });
export const UpdateModelProviderSchema = z.object({ name: Name.optional(), baseUrl: BaseUrl.nullable().optional(), apiKey: ApiKey.optional() });
export const TestModelProviderSchema = z.object({ model: ModelId });
export const ModelProviderTestResultSchema = z.object({ ok: z.boolean(), detail: z.string(), latencyMs: z.number().int().nullable(), servedModel: z.string().nullable(), provider: ModelProviderSchema });
export type ModelProviderTestResult = z.infer<typeof ModelProviderTestResultSchema>;

export const ModelAssignmentSchema = z.object({
  providerId: Id,
  model: ModelId,
  temperature: z.number().min(0).max(2).nullable(),
  maxOutputTokens: z.number().int().min(64).max(32_000).nullable(),
});
export type ModelAssignment = z.infer<typeof ModelAssignmentSchema>;

/** null = the purpose follows the environment fallback (LLM_PROVIDER / LLM_MODEL). */
export const ModelAssignmentsSchema = z.object({
  whatsapp_agent: ModelAssignmentSchema.nullable(),
  voice_agent: ModelAssignmentSchema.nullable(),
  summaries: ModelAssignmentSchema.nullable(),
  audits: ModelAssignmentSchema.nullable(),
  vision: ModelAssignmentSchema.nullable(),
  /** Absent in releases published before the copilot existed → null (environment fallback). */
  copilot: ModelAssignmentSchema.nullable().default(null),
});
export type ModelAssignments = z.infer<typeof ModelAssignmentsSchema>;

export const ModelConfigReleaseSchema = GlobalReleaseSummarySchema.extend({ content: z.object({ assignments: ModelAssignmentsSchema }) });
export type ModelConfigRelease = z.infer<typeof ModelConfigReleaseSchema>;

export const SaveModelConfigDraftSchema = z.object({ assignments: ModelAssignmentsSchema, changeNote: z.string().max(500).nullable().optional() });

/** What each purpose resolves to right now (published assignment, else the environment fallback). */
export const EffectiveModelSchema = z.object({
  purpose: ModelPurposeSchema,
  source: z.enum(['assignment', 'environment', 'none']),
  state: LlmStateSchema,
  provider: z.string().nullable(),
  providerName: z.string().nullable(),
  model: z.string().nullable(),
  reason: z.string().nullable(),
});
export type EffectiveModel = z.infer<typeof EffectiveModelSchema>;

export const ModelUsageRowSchema = z.object({ provider: z.string().nullable(), model: z.string().nullable(), inputTokens: z.number(), outputTokens: z.number(), estimated: z.boolean() });

export const ModelAdminOverviewSchema = z.object({
  providers: z.array(ModelProviderSchema),
  published: ModelConfigReleaseSchema.nullable(),
  draft: ModelConfigReleaseSchema.nullable(),
  history: z.array(GlobalReleaseSummarySchema),
  effective: z.array(EffectiveModelSchema),
  /** Platform-wide model tokens of the last 30 days by provider/model (no tenant detail). */
  usage: z.object({ since: Iso, rows: z.array(ModelUsageRowSchema) }),
  /** Workspaces where the caller is manager or above: scenario suites can only run there. */
  testWorkspaces: z.array(z.object({ id: Id, name: z.string(), scenarios: z.number().int() })),
});
export type ModelAdminOverview = z.infer<typeof ModelAdminOverviewSchema>;

export const ModelScenarioTargetSchema = z.discriminatedUnion('kind', [
  /** Whatever the purpose resolves to today. */
  z.object({ kind: z.literal('effective') }),
  /** The saved draft's assignment for the purpose. */
  z.object({ kind: z.literal('draft') }),
  /** An ad-hoc candidate that is not saved anywhere. */
  z.object({ kind: z.literal('candidate'), assignment: ModelAssignmentSchema }),
]);

export const RunModelScenariosSchema = z.object({
  workspaceId: Id,
  purpose: z.enum(['whatsapp_agent', 'voice_agent']),
  target: ModelScenarioTargetSchema,
  scenarioIds: z.array(Id).min(1).max(20).optional(),
});

export const ModelScenarioRunSchema = z.object({
  target: z.object({ kind: z.enum(['effective', 'draft', 'candidate']), provider: z.string().nullable(), providerName: z.string().nullable(), model: z.string().nullable(), simulated: z.boolean() }),
  summary: z.object({
    scenarios: z.number().int(),
    passed: z.number().int(),
    failed: z.number().int(),
    errors: z.number().int(),
    turns: z.number().int(),
    /** Model time per turn (one turn may include several model calls). null = no turn reached the model. */
    modelMsP50: z.number().int().nullable(),
    modelMsP95: z.number().int().nullable(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
  }),
  runs: z.array(TestRunSchema),
});
export type ModelScenarioRun = z.infer<typeof ModelScenarioRunSchema>;
